import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppState } from "../../app-state";
import { resolveGallerySpecificSat } from "../../lib/api";
import { SAT_SUPPLY } from "../../lib/rarity";
import type { OrdinalSource, SpecificSatResolution } from "../../lib/types";
import type { SpecificSatPickerProps } from "./SpecificSatPicker";

const INSCRIPTION_ID_PATTERN = /^[0-9a-f]{64}i[0-9]{1,10}$/i;
const SATPOINT_PATTERN = /^[0-9a-f]{64}:[0-9]{1,10}:[0-9]{1,16}$/i;

export function specificSatReferenceError(value: string): string {
  const reference = value.trim();
  if (!reference) return "Enter a decimal sat number";
  if (/^[0-9]+$/.test(reference)) {
    const sat = Number(reference);
    return Number.isSafeInteger(sat) && sat < SAT_SUPPLY ? "" : "Enter a sat number within the Bitcoin supply";
  }
  if (INSCRIPTION_ID_PATTERN.test(reference) || SATPOINT_PATTERN.test(reference)) return "";
  return "Enter a decimal sat number, inscription ID (64 hex + i + index), or exact satpoint (txid:vout:offset).";
}

export function useSpecificSatSelection() {
  const { authorizeWallet, setWalletModalOpen, wallet } = useAppState();
  const [input, setInputValue] = useState("");
  const [advanced, setAdvancedValue] = useState(false);
  const [resolution, setResolution] = useState<SpecificSatResolution | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [parentRelation, setParentRelationValue] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef(0);

  const clear = useCallback(() => {
    requestRef.current += 1;
    setInputValue("");
    setAdvancedValue(false);
    setResolution(null);
    setConfirmed(false);
    setParentRelationValue(false);
    setLoading(false);
    setError("");
  }, []);

  useEffect(() => clear(), [clear, wallet?.ordinals.address]);

  const setInput = useCallback((value: string) => {
    requestRef.current += 1;
    setInputValue(value);
    setResolution(null);
    setConfirmed(false);
    setParentRelationValue(false);
    setLoading(false);
    setError("");
  }, []);

  const setAdvanced = useCallback((value: boolean) => {
    requestRef.current += 1;
    setAdvancedValue(value);
    setInputValue("");
    setResolution(null);
    setConfirmed(false);
    setParentRelationValue(false);
    setLoading(false);
    setError("");
  }, []);

  const resolve = useCallback(async () => {
    const reference = input.trim();
    if (!reference) return;
    const formatError = specificSatReferenceError(reference);
    if (formatError) {
      setError(formatError);
      return;
    }
    if (!wallet) {
      setWalletModalOpen(true);
      return;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    setResolution(null);
    setConfirmed(false);
    setParentRelationValue(false);
    try {
      const session = await authorizeWallet(true);
      if (!session) throw new Error("Connect and verify both wallet addresses to resolve a specific sat");
      const next = await resolveGallerySpecificSat(session, reference);
      if (requestId !== requestRef.current) return;
      setResolution(next);
    } catch (caught) {
      if (requestId === requestRef.current) setError(caught instanceof Error ? caught.message : "The specific sat could not be resolved");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [authorizeWallet, input, setWalletModalOpen, wallet]);

  const setParentRelation = useCallback((value: boolean) => {
    setParentRelationValue(value);
    setConfirmed(false);
  }, []);

  const source = useMemo<OrdinalSource | undefined>(() => confirmed && resolution ? {
    ...resolution.source,
    parentRelation,
  } : undefined, [confirmed, parentRelation, resolution]);

  return {
    source,
    confirmed,
    clear,
    pickerProps: {
      walletConnected: Boolean(wallet),
      advanced,
      input,
      resolution,
      confirmed,
      parentRelation,
      loading,
      error,
      onInput: setInput,
      onAdvanced: setAdvanced,
      onResolve: () => { void resolve(); },
      onParentRelation: setParentRelation,
      onConfirm: () => setConfirmed(true),
    } satisfies SpecificSatPickerProps,
  };
}
